import { useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { EstadoAtuacao } from "@/hooks/useAtuacao";
import { statePaths, stateCenters, decorativeFragments } from "./brazilMapData";

interface BrazilMapProps {
  estados: EstadoAtuacao[];
  onSelectEstado?: (sigla: string | null) => void;
  selectedEstado?: string | null;
  compact?: boolean;
  highlightedRegiao?: string | null;
}

const BrazilMap = ({ estados, onSelectEstado, selectedEstado, compact, highlightedRegiao }: BrazilMapProps) => {
  const [hoveredUf, setHoveredUf] = useState<string | null>(null);

  const activeUfs = new Set(estados.map((e) => e.sigla));
  const estadoMap = Object.fromEntries(estados.map((e) => [e.sigla, e]));

  const getStateFill = (uf: string) => {
    if (!activeUfs.has(uf)) return "hsl(var(--muted))";
    const estado = estadoMap[uf];
    if (highlightedRegiao && estado?.regiao !== highlightedRegiao) return "hsl(var(--muted))";
    if (selectedEstado === uf) return "hsl(var(--accent))";
    if (hoveredUf === uf) return "hsl(213 60% 35%)";
    if (uf === "GO") return "hsl(213 60% 28%)";
    return "hsl(var(--primary))";
  };

  const getStateStroke = (uf: string) => {
    if (selectedEstado === uf) return "hsl(var(--accent-foreground))";
    if (uf === "GO" && activeUfs.has(uf)) return "hsl(var(--accent))";
    return "hsl(var(--border))";
  };

  const viewBox = "0 0 620 645";

  return (
    <svg
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      className={`w-full ${compact ? "max-w-md" : "max-w-2xl"} mx-auto`}
      role="img"
      aria-label="Mapa de atuação da Acesso no Brasil"
    >
      {/* Decorative fragments (islands) */}
      {decorativeFragments.map((d, i) => (
        <path
          key={`frag-${i}`}
          d={d}
          fill="hsl(var(--muted))"
          stroke="hsl(var(--border))"
          strokeWidth={0.5}
          className="pointer-events-none"
        />
      ))}

      {/* State shapes */}
      {Object.entries(statePaths).map(([uf, shape]) => {
        const isActive = activeUfs.has(uf);
        const estado = estadoMap[uf];

        const shapeProps = {
          fill: getStateFill(uf),
          stroke: getStateStroke(uf),
          strokeWidth: selectedEstado === uf || uf === "GO" ? 2 : 1,
          className: `transition-all duration-200 ${isActive ? "cursor-pointer" : ""}`,
          style: uf === "GO" && isActive ? { filter: "drop-shadow(0 0 6px hsl(152 55% 42% / 0.4))" } : undefined,
          onMouseEnter: () => isActive && setHoveredUf(uf),
          onMouseLeave: () => setHoveredUf(null),
          onClick: () => isActive && onSelectEstado?.(selectedEstado === uf ? null : uf),
        };

        const shapeElement = (
          <path key={uf} data-uf={uf} d={shape.data} {...shapeProps} />
        );

        if (isActive && estado) {
          return (
            <Tooltip key={uf}>
              <TooltipTrigger asChild>{shapeElement}</TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                <p className="font-semibold">{estado.nome_estado}</p>
                <p>{estado.total_clientes} clientes • Desde {estado.ano_inicio}</p>
                <p className="text-muted-foreground">{estado.tipo_operacao}</p>
              </TooltipContent>
            </Tooltip>
          );
        }

        return shapeElement;
      })}

      {/* State labels */}
      {Object.entries(stateCenters).map(([uf, pos]) => (
        <text
          key={`label-${uf}`}
          x={pos.x}
          y={pos.y}
          textAnchor="middle"
          dominantBaseline="central"
          className="pointer-events-none select-none"
          fontSize={compact ? 10 : 12}
          fontWeight={activeUfs.has(uf) ? 700 : 400}
          fill={activeUfs.has(uf) ? "hsl(var(--primary-foreground))" : "hsl(var(--muted-foreground))"}
        >
          {uf}
        </text>
      ))}
    </svg>
  );
};

export default BrazilMap;
